import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface Site {
  id: string
  domain: string
  baseUrl: string
  verified: boolean
  verifyMethod: 'dns-txt' | 'meta' | 'file' | 'gsc-oauth' | null
  verifyToken: string
  businessType: string
}
interface Run { id: string; status: string; score: number | null; createdAt: string; pagesCrawled: number }
type VerifyMethod = 'dns-txt' | 'meta' | 'file'
interface CheckResult { verified: boolean; method: string; detail?: string | null }

export function SiteDetailPage() {
  const t = useT()
  const { siteId = '' } = useParams()
  const qc = useQueryClient()

  const site = useQuery({ queryKey: ['site', siteId], queryFn: () => apiClient.get<Site>(`/sites/${siteId}`) })
  const runs = useQuery({ queryKey: ['runs', siteId], queryFn: () => apiClient.get<{ data: Run[] }>(`/sites/${siteId}/runs`) })

  const [method, setMethod] = useState<VerifyMethod>('dns-txt')
  const [checkMsg, setCheckMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const setVerifyMethod = useMutation({
    mutationFn: (m: VerifyMethod) => apiClient.post(`/sites/${siteId}/verify-ownership`, { method: m }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site', siteId] }),
  })

  const checkVerification = useMutation({
    mutationFn: () => apiClient.post<CheckResult>(`/sites/${siteId}/check-verification`, {}),
    onSuccess: (res) => {
      if (res.verified) {
        setCheckMsg({ ok: true, text: t('site.detail.verify.ok') })
        qc.invalidateQueries({ queryKey: ['site', siteId] })
      } else {
        const detail = res.detail || ''
        setCheckMsg({
          ok: false,
          text: detail
            ? t('site.detail.verify.fail').replace('{detail}', detail)
            : t('site.detail.verify.fail.generic'),
        })
      }
    },
    onError: () => setCheckMsg({ ok: false, text: t('site.detail.verify.fail.generic') }),
  })

  const startScan = useMutation({
    mutationFn: () => apiClient.post<Run>(`/sites/${siteId}/runs`, { profile: 'quick' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['runs', siteId] }),
  })

  if (site.isLoading) return <p>{t('common.loading')}</p>
  if (site.isError || !site.data) return <p>{t('common.error')}</p>

  const s = site.data
  const last = runs.data?.data[0]
  const activeMethod: VerifyMethod = (s.verifyMethod === 'gsc-oauth' || !s.verifyMethod) ? method : (s.verifyMethod as VerifyMethod)
  const dnsHost = `_discover-verify.${s.domain}`
  const dnsValue = `discover-verify=${s.verifyToken}`
  const metaTag = `<meta name="discover-verify" content="${s.verifyToken}">`
  const filePath = `/.well-known/discover-verify-${s.verifyToken}`

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">{s.domain}</h1>
        <button
          type="button"
          onClick={() => startScan.mutate()}
          disabled={startScan.isPending || !s.verified}
          title={!s.verified ? t('site.detail.runscan.notverified') : undefined}
          className="px-3 py-1.5 text-sm rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'var(--color-accent)', color: 'white' }}
        >
          {t('site.detail.runscan')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs opacity-70">{t('site.detail.score')}</div>
          <div className="text-3xl font-semibold mt-1">{last?.score ?? '—'}</div>
        </div>
        <div className="p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs opacity-70">{t('site.detail.business')}</div>
          <div className="text-sm mt-1">{s.businessType}</div>
        </div>
        <div className="p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs opacity-70">{t('site.detail.verified')}</div>
          <div className="text-sm mt-1 flex items-center gap-1.5">
            {s.verified ? (
              <>
                <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-accent)' }} />
                <span>{t('site.detail.verified.yes')}</span>
              </>
            ) : (
              <>
                <Clock className="w-4 h-4 opacity-70" />
                <span>{t('site.detail.verified.no')}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {!s.verified && (
        <div className="mb-6 p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <h2 className="text-base font-semibold mb-1">{t('site.detail.verify.title')}</h2>
          <p className="text-sm opacity-80 mb-3">{t('site.detail.verify.intro')}</p>

          <label className="block text-xs opacity-70 mb-1" htmlFor="verify-method">
            {t('site.detail.verify.method')}
          </label>
          <select
            id="verify-method"
            className="text-sm px-2 py-1 rounded border mb-4"
            style={{ borderColor: 'var(--color-border)', background: 'transparent' }}
            value={activeMethod}
            onChange={(e) => {
              const m = e.target.value as VerifyMethod
              setMethod(m)
              setVerifyMethod.mutate(m)
              setCheckMsg(null)
            }}
          >
            <option value="dns-txt">{t('onboard.verify.dns-txt')}</option>
            <option value="meta">{t('onboard.verify.meta')}</option>
            <option value="file">{t('onboard.verify.file')}</option>
          </select>

          {activeMethod === 'dns-txt' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <div className="text-xs opacity-70 mb-1">{t('site.detail.verify.dns.host')}</div>
                <code className="block text-xs p-2 rounded border break-all" style={{ borderColor: 'var(--color-border)' }}>{dnsHost}</code>
              </div>
              <div>
                <div className="text-xs opacity-70 mb-1">{t('site.detail.verify.dns.value')}</div>
                <code className="block text-xs p-2 rounded border break-all" style={{ borderColor: 'var(--color-border)' }}>{dnsValue}</code>
              </div>
            </div>
          )}

          {activeMethod === 'meta' && (
            <div className="mb-4">
              <div className="text-xs opacity-70 mb-1">{t('site.detail.verify.meta.tag')}</div>
              <code className="block text-xs p-2 rounded border break-all" style={{ borderColor: 'var(--color-border)' }}>{metaTag}</code>
            </div>
          )}

          {activeMethod === 'file' && (
            <div className="mb-4">
              <div className="text-xs opacity-70 mb-1">{t('site.detail.verify.file.path')}</div>
              <code className="block text-xs p-2 rounded border break-all" style={{ borderColor: 'var(--color-border)' }}>{filePath}</code>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => { setCheckMsg(null); checkVerification.mutate() }}
              disabled={checkVerification.isPending}
              className="px-3 py-1.5 text-sm rounded font-medium disabled:opacity-50"
              style={{ background: 'var(--color-accent)', color: 'white' }}
            >
              {checkVerification.isPending ? t('site.detail.verify.checking') : t('site.detail.verify.check')}
            </button>
            {checkMsg && (
              <span className="text-sm flex items-center gap-1.5" style={{ color: checkMsg.ok ? 'var(--color-accent)' : '#dc2626' }}>
                {checkMsg.ok
                  ? <CheckCircle2 className="w-4 h-4" />
                  : <AlertCircle className="w-4 h-4" />}
                <span>{checkMsg.text}</span>
              </span>
            )}
          </div>
        </div>
      )}

      <h2 className="text-sm font-medium opacity-80 mb-2">{t('site.detail.runs')}</h2>
      {runs.isLoading && <p>{t('common.loading')}</p>}
      {runs.data && runs.data.data.length > 0 && (
        <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
          {runs.data.data.map((r) => (
            <li key={r.id} className="py-2 flex items-center justify-between">
              <Link to={`/sites/${siteId}/runs/${r.id}`} className="hover:underline text-sm">
                {new Date(r.createdAt).toLocaleString()}
              </Link>
              <span className="text-xs opacity-70">
                {t(`run.status.${r.status}`)} · {r.pagesCrawled} pages · score {r.score ?? '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
